"use client";

import { type ReactNode, useCallback, useMemo } from "react";

import {
  LocationProvider,
  useLocationContext,
  type HeroLocation,
} from "./location-context";
import { SearchProvider, useSearchContext } from "./search-context";
import { useFilterState } from "@/hooks/use-filter-state";
import { ResultsProvider, useResultsContext } from "./results-context";
import {
  MapInteractionProvider,
  useMapInteractionContext,
} from "./map-interaction-context";
import { UIProvider, useUIContext } from "./ui-context";
import { buildHeroMapContextValue } from "./hero-map-context-value";

export interface HeroMapInitialSearch {
  location?: HeroLocation;
  quantity?: number;
  radiusMiles?: number;
}

export function HeroMapProvider({
  children,
  initialSearch,
}: {
  children: ReactNode;
  initialSearch?: HeroMapInitialSearch;
}) {
  return (
    <LocationProvider initialLocation={initialSearch?.location ?? null}>
      <SearchProvider
        initialQuantity={initialSearch?.quantity}
        initialRadiusMiles={initialSearch?.radiusMiles}
      >
        <ResultsProvider>
          <MapInteractionProvider>
            <UIProvider>{children}</UIProvider>
          </MapInteractionProvider>
        </ResultsProvider>
      </SearchProvider>
    </LocationProvider>
  );
}

export function useHeroMapContext() {
  const location = useLocationContext();
  const search = useSearchContext();
  const filter = useFilterState();
  const results = useResultsContext();
  const mapInteraction = useMapInteractionContext();
  const uiContext = useUIContext();

  const clearSearch = useCallback(() => {
    location.clearLocation();
    search.resetSearch();
    filter.resetFilters();
    results.setProducts([]);
    results.setMetadata(null);
    results.setIsLoading(false);
    results.setError(null);
    mapInteraction.setHoveredProductId(null);
    mapInteraction.setSelectedProductId(null);
    mapInteraction.setViewportCenter(null);
    mapInteraction.setOptimizedZoom(null);
    uiContext.setFlowStage("search");
    uiContext.setFiltersExpanded(false);
    uiContext.setListingPreview(null);
    uiContext.setViewportLocationName(null);
  }, [location, search, filter, results, mapInteraction, uiContext]);

  return useMemo(
    () =>
      buildHeroMapContextValue(
        location,
        search,
        filter,
        results,
        mapInteraction,
        uiContext,
        clearSearch,
      ),
    [location, search, filter, results, mapInteraction, uiContext, clearSearch],
  );
}
